import { useEffect, useRef, useState } from 'react';
import Select from "react-select";
import { ClipboardPlus, Copy, Loader, Mic } from 'lucide-react';
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { getPractice, getSecureData, notify } from '../../helpers/index.js';
import { Config } from '../../Config.js';
import Spacing from '../Spacing.jsx';
import BackBtn from '../Button/BackBtn.jsx';
import DocflowRecordingModal from '../Modal/DocflowRecordingModal.jsx';
import { userAccessToken } from "../../helpers/index.js";


export default function PromptPlayground({ selectedPracticeChange, fetchChangePracticeData }) {
    let location_id = getPractice("location_id");
    let account_id = getPractice("account_id");
    if (selectedPracticeChange != null && fetchChangePracticeData) {
        location_id = selectedPracticeChange?.location_id;
        account_id = selectedPracticeChange?.account_id;
    }
    const messageBoxRef = useRef(null);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [userInput, setUserInput] = useState("");
    const [systemInput, setSystemInput] = useState("");
    const [messages, setMessages] = useState([]);
    const [openRecordingModal, setOpenRecordingModal] = useState(false);
    const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
    const [ans, setAns] = useState("");
    const [selectedSpeciality, setSelectedSpeciality] = useState(null);

    useEffect(() => {
        const fetchSpeciality = async () => {
            const info = await getSecureData("practiceSpecialityInfo");
            if (info) {
                setSelectedSpeciality({ value: info.id, label: info.name });
            }
        };
        fetchSpeciality();
    }, []);

    const handleSpecialityChange = (selectedOption) => {
        setSelectedSpeciality(selectedOption);
    };

    // Handle message submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedUserInput = userInput.trim();
        const trimmedSystemInput = systemInput.trim();

        if (!trimmedUserInput || !trimmedSystemInput || !selectedSpeciality?.value) return;

        setButtonLoader(true);

        // Add user message to chat
        setMessages((prevMessages) => [
            ...prevMessages,
            { type: "user", content: trimmedUserInput },
            { type: "ai", content: "Thinking..." },
        ]);

        try {
            const response = await fetch(`${Config.apiServerUrl}/ai/prompt`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${userAccessToken()}`,
                },
                body: JSON.stringify({
                    account_id: account_id,
                    location_id: location_id,
                    user_input: trimmedUserInput,
                    system_input: trimmedSystemInput,
                    speciality_id: selectedSpeciality.value,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let done = false;
            let finalResponse = "";

            while (!done) {
                const { value, done: readerDone } = await reader.read();
                done = readerDone;

                if (value) {
                    const chunk = decoder.decode(value, { stream: true });
                    finalResponse += chunk;

                    setMessages((prevMessages) => {
                        const updated = [...prevMessages];
                        updated[updated.length - 1] = { type: "ai", content: finalResponse };
                        return updated;
                    });
                }
            }

            setAns(finalResponse);

            // Fix formatting
            document.querySelectorAll(".docpro_message_item_text:last-of-type").forEach((el) => {
                el.innerHTML = el.innerHTML
                    .replace("```html", "")
                    .replace("```", "")
                    .replace("&lt;/<", "<");
            });
        } catch (error) {
            console.error("Error fetching response:", error);
            setMessages((prevMessages) => [
                ...prevMessages.slice(0, -1),
                { type: "ai", content: "An error occurred while fetching the response." },
            ]);
        } finally {
            setButtonLoader(false);
        }
    };


    useEffect(() => {
        if (messageBoxRef.current) {
            messageBoxRef.current.scrollTop = messageBoxRef.current.scrollHeight;
        }
    }, [messages]);

    const handleCopy = (id) => {
        const content = document.querySelector('.GPT_response_' + id);
        const range = document.createRange();
        range.selectNode(content);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        document.execCommand('copy');
        window.getSelection().removeAllRanges();

        notify("success", "Content copied to clipboard!", 0.5);
    };
    const handleSavePrompt = () => {
        const formData = new FormData();
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const blob = new Blob([ans], { type: "text/plain" });
        formData.append("file", blob, `prompt-${timestamp}.txt`);

        fetch(`${Config.apiServerUrl}/upload`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${userAccessToken()}`,
                // 🚫 Do NOT set Content-Type manually for FormData
            },
            body: formData,
        })
        .then(async res => {
            console.log("Response status:", res.status);
            await res.json();
        })
        .catch(err => {
            console.error("Save failed:", err);
        });
    };



    useDocumentTitle("DocFlow - Smart Chart");

    return (
        <>
            <BackBtn href="/ai-charting" btnText="Back to AI charting" />
            <Spacing lg="0" sm="24" />
            <div className="docpro_page_heading_wrap">
                <h2 className="docpro_page_heading docpro_fs_24">
                    <i className="docpro_iris_bg docpro_white_color">
                        <ClipboardPlus width={20} height={20} />
                    </i>
                    Prompt Playground
                </h2>
            </div>

            <div className="docpro_grid docpro_grid_gap_20 docpro_grid_col_1_3">
                <div className='docpro_data_table_wrap'>
                    <div className='docpro_data_table_headings'>
                        <h3 className='docpro_mb_0 docpro_fs_18 docpro_fw_600 mx-1'>Instructions</h3>
                    </div>
                    <hr />
                    <div className='p-3 mx-1 mb-2'>
                        <form onSubmit={handleSubmit}>
                            <div className='flex GPT_instructions'>
                                <div>
                                    <label
                                        className="docpro_fw_500 docpro_primary_color flex items-center"
                                        style={{ justifyContent: 'space-between' }}
                                    >
                                        <span>
                                            Select Speciality <span className="docpro_red_color">*</span>
                                        </span>
                                    </label>

                                    <Select
                                        required
                                        options={selectedSpeciality ? [selectedSpeciality] : []}
                                        placeholder="Speciality..."
                                        isSearchable={true}
                                        // isDisabled
                                        value={selectedSpeciality}
                                        onChange={handleSpecialityChange}
                                        className="react-select-container w-full"
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                                borderColor: 'var(--border)',
                                                borderRadius: '8px',
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                zIndex: 1050,
                                                marginTop: '-2px',
                                                borderRadius: '0 0 3px 3px',
                                            }),
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 1050,
                                                padding: 0,
                                            }),
                                        }}
                                    />
                                    <Spacing lg="20" sm="20" />

                                    <label className="docpro_fw_500 docpro_primary_color flex items-center" style={{ justifyContent: 'space-between' }}>
                                        <span>System Input <span className="docpro_red_color">*</span></span>
                                    </label>
                                    <textarea
                                        required
                                        minLength={2}
                                        value={systemInput}
                                        onChange={(e) => setSystemInput(e.target.value)}
                                        className="docpro_form_field resize-none"
                                        cols="30"
                                        rows="6"
                                        placeholder="Enter system message..."
                                    ></textarea>
                                    <Spacing lg="20" sm="20" />

                                    <label className="docpro_fw_500 docpro_primary_color flex items-center" style={{ justifyContent: 'space-between' }}>
                                        <span>User Input <span className="docpro_red_color">*</span></span>
                                        <button type='button'
                                            className="docpro_icon_btn overview record" style={{ width: 'auto', height: 'auto', borderRadius: '50%' }}
                                            onClick={() => {
                                                setOpenRecordingModal(true);
                                                setPreviousScrollPosition(window.scrollY);
                                                window.scrollTo(0, 0);
                                            }}
                                        >
                                            <Mic width={20} height={20} />
                                            <span>Record</span>
                                            <span className='docpro_tip'>Voice Transcription</span>
                                        </button>
                                    </label>
                                    <textarea
                                        required
                                        minLength={2}
                                        value={userInput}
                                        onChange={(e) => setUserInput(e.target.value)}
                                        className="docpro_form_field resize-none"
                                        cols="30"
                                        rows="6"
                                        placeholder="Enter user message..."
                                    ></textarea>
                                    <Spacing lg="20" sm="20" />
                                </div>

                                <div className="docpro_modal_btns" style={{ marginTop: 'auto', justifyContent: 'space-between' }}>
                                    <button
                                        type='reset'
                                        className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                                        // // style={{ backgroundColor: '#ff2b50', borderColor: '#ff2b50' }}
                                        // onClick={() => { setUserInput(""); setDocflowTemplate(null); }}
                                        onClick={handleSavePrompt}
                                    >
                                        <span>Save Prompt</span>
                                    </button>
                                    <button
                                        type='submit'
                                        className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                                        disabled={buttonLoader ? true : false}
                                    >
                                        <span>Test Prompt</span>
                                        {buttonLoader && (
                                            <span className="docpro_loading">
                                                <Loader width={20} height={20} />
                                            </span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className='docpro_data_table_wrap'>
                    <div className='docpro_data_table_headings'>
                        <h3 className='docpro_mb_0 docpro_fs_18 docpro_fw_600 mx-1'>Assistant Result</h3>
                    </div>
                    <hr />
                    <div className='p-3 mx-1 mb-2'>
                        <div className='message-box' style={{ marginBottom: '0' }} ref={messageBoxRef}>
                            {messages.map((message, index) => (
                                <div key={index} className='docflow_chat'>
                                    {message.type === "user" ?
                                        <div className="right-msg-data" style={{ backgroundColor: 'e9e9e954' }}>
                                            <div className={`flex-grow-3 GPT_response_${index}`} style={{ whiteSpace: 'pre-wrap', color: 'var(--primary)' }}>
                                                {message.content}
                                            </div>
                                        </div>
                                        :
                                        <div className="left-msg-data" >
                                            <div className="shoutbox-flex">
                                                <div className="docpro_message_item_avatar shoutbox_img_avatar">
                                                    AI{" "}
                                                </div>
                                                <div className='relative'>
                                                    <div className='docpro_message_item_text' style={{ backgroundColor: '#f6f6f6' }}>
                                                        <span className={`GPT_response_${index}`} dangerouslySetInnerHTML={{ __html: message.content }} />
                                                    </div>
                                                    {message.content && (
                                                        <button className='absolute top-0 right-0 docpro_bordered_btn docpro_fw_600 docpro_filed_btn copy' onClick={() => handleCopy(index)}><Copy height={16} /></button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {openRecordingModal && (
                <DocflowRecordingModal
                    activeModal={openRecordingModal}
                    setActiveModal={setOpenRecordingModal}
                    setTranscribedText={setUserInput}
                    previousScrollPosition={previousScrollPosition}
                />
            )}
        </>
    );
}
